

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewInsuranceModal from 'components/NewInsuranceModal';

const Insurances = () => {
    const [insurances, setInsurances] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('insurances');

    useEffect(() => {
        fetchInsurances();
    }, []);

    const fetchInsurances = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/insurances/');
            setInsurances(response.data);
        } catch (error) {
            console.error('Error fetching insurances:', error);
            setSnackbar({ open: true, message: 'Error loading insurances', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveInsurance = async (payload) => {
        try {
            await axiosInstance.post('/insurances/', payload);
            setSnackbar({ open: true, message: 'Insurance created successfully', severity: 'success' });
            fetchInsurances();
        } catch (error) {
            console.error('Error saving insurance:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/insurance/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Insurance Number",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Full Name",
            accessor: "full_name",
            width: "15%"
        },
        {
            Header: "Policy Number",
            accessor: "policy_number",
            width: "15%"
        },
        {
            Header: "Provider",
            accessor: "provider",
            width: "12%"
        },
        {
            Header: "Premium",
            accessor: "premium",
            width: "10%",
            Cell: ({ value }) => value ? `$${value}` : '-'
        },
        {
            Header: "Coverage Amount",
            accessor: "coverage_amount",
            width: "12%",
            Cell: ({ value }) => value ? `$${value}` : '-'
        },
        {
            Header: "Issue Date",
            accessor: "issue_date",
            width: "10%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
        {
            Header: "Expiry Date",
            accessor: "expiry_date",
            width: "11%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: insurances,
    }), [insurances]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Insurances</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Insurance
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : insurances.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Insurance" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewInsuranceModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveInsurance}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default Insurances;

